import { Field, FieldProps, Form, Formik } from 'formik';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { TeamMemberData, UserRolesData, UserRolesName } from 'types';
import { Divider, Button, RadioGroup, FormControlLabel, DialogActions } from '@mui/material';
import { useQuery } from '@redux-requests/react';
import { addUser, fetchActiveUserOrganization, getRoles } from 'store/myAccount/requests';
import { GVTypography } from 'components/lib';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVRadio from 'components/lib/GVRadio/GVRadio';
import { getAssignableRoles, isSuperAdmin } from 'utils/auth';
import AuditTrailPermissionField from './AuditTrailPermissionField';
import RoleHeader from './RoleHeader';
import { isUserPrimaryRoleSelected } from './auditTrailPermission';
import { useUserStateStore } from 'zstore/userStateStore';
import { User } from '@auth0/auth0-react';
import { useInviteMembersMutation } from 'store/queries/organizations/useInviteMembersMutation';
import styles from './AddUserSingle.module.css';

type SingleUserAddProps = {
  ssoEnabled?: boolean;
  setModalOpen: (open: boolean) => void;
  onClose: () => void;
};

const SingleUserAdd = ({ ssoEnabled, setModalOpen, onClose }: SingleUserAddProps) => {
  const dispatch = useDispatch();
  const { mutate } = useInviteMembersMutation();
  const fetchRoles = useQuery(getRoles());
  const roles: Array<UserRolesData> = fetchRoles.data || [];
  const roleUserId = roles.find((role) => role.name === 'user')?.id;
  const { data: orgData } = useQuery({ type: fetchActiveUserOrganization });

  const [initialValues, setInitialValues] = useState<TeamMemberData>({
    // users belonging to an sso enabled tenant will not have firstName/lastName
    ...(!ssoEnabled && { firstName: '', lastName: '' }),
    role: roleUserId,
    email: '',
    hasAuditTrail: false,
  });

  const { user: auth0User } = useUserStateStore();
  const superAdmin = isSuperAdmin(auth0User as User);

  const validateForm = (values: TeamMemberData) => {
    const errors: Partial<TeamMemberData> = {};
    if ('firstName' in values && !values.firstName?.trim()) {
      errors.firstName = 'Required field';
    }
    if ('lastName' in values && !values.lastName?.trim()) {
      errors.lastName = 'Required field';
    }
    if (!values.email.trim()) {
      errors.email = 'Required field';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    return errors;
  };

  const userIsFromConnection = (email: string): boolean => {
    if (
      !orgData?.connection?.configurationSettings?.domain ||
      orgData?.connection?.configurationSettings?.domain.length <= 0
    ) {
      return false;
    }
    const userDomain = email.split('@')[1];
    return orgData.connection?.configurationSettings?.domain.some((domain) => domain === userDomain);
  };

  const handleSubmit = (values: TeamMemberData) => {
    if (
      orgData?.connection &&
      orgData?.connection.connectionName !== 'Username-Password-Authentication' &&
      userIsFromConnection(values.email)
    ) {
      mutate({
        email: values.email,
        roleId: values.role || '',
        orgId: orgData?.id,
        hasAuditTrail: values.hasAuditTrail,
        roleName: roles.find((role: UserRolesData) => role.id === values.role)?.name || '',
      });
    } else if (orgData?.id) {
      dispatch(addUser(orgData.id, values, roles));
    }

    setModalOpen(false);
    onClose();
  };

  return (
    <Formik
      key="singleUserForm"
      initialValues={initialValues}
      enableReinitialize
      validate={validateForm}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form id="add-team-member-form" onSubmit={formikProps.handleSubmit} className={styles.form}>
          <div className={styles.section}>
            <GVTypography variant="subtitle1" fontWeight={400}>
              Team member info
            </GVTypography>
          </div>
          {!ssoEnabled && (
            <div className={styles.fields}>
              <Field name="firstName">
                {(fieldProps: FieldProps) => (
                  <GVTextField
                    name={fieldProps.field.name}
                    hasError={!!fieldProps.meta.error}
                    message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                    value={fieldProps.field.value}
                    onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                    label="First Name"
                    id="first-name-member"
                    data-testid="first_name_member"
                  />
                )}
              </Field>
              <Field name="lastName">
                {(fieldProps: FieldProps) => (
                  <GVTextField
                    name={fieldProps.field.name}
                    hasError={!!fieldProps.meta.error}
                    message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                    value={fieldProps.field.value}
                    onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                    id="last-name-member"
                    data-testid="last_name_member"
                    label="Last Name"
                  />
                )}
              </Field>
            </div>
          )}
          <Field name="email">
            {(fieldProps: FieldProps) => (
              <GVTextField
                name={fieldProps.field.name}
                hasError={!!fieldProps.meta.error}
                message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                value={fieldProps.field.value}
                onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                id="email-member"
                data-testid="email_member"
                label="Email"
              />
            )}
          </Field>

          <Divider light className={styles.rule} />

          <RoleHeader className={styles.role} />
          <Field name="role">
            {(fieldProps: FieldProps) => (
              <RadioGroup
                aria-label="role"
                name={fieldProps.field.name}
                value={fieldProps.field.value}
                onChange={(event) => {
                  fieldProps.field.onChange(event);
                  if (!isUserPrimaryRoleSelected(event.target.value, roles)) {
                    formikProps.setFieldValue('hasAuditTrail', false);
                  }
                }}
                row
              >
                {getAssignableRoles(roles, superAdmin).map((role) => {
                  return (
                    <FormControlLabel
                      className={styles.roles}
                      key={role.id}
                      value={role.id}
                      control={<GVRadio color="secondary" className={styles.radio} />}
                      label={UserRolesName[role.name]}
                    />
                  );
                })}
              </RadioGroup>
            )}
          </Field>
          <AuditTrailPermissionField roleId={formikProps.values.role} roles={roles} />
          <Divider light className={styles.rule} />
          <DialogActions>
            <Button color="inherit" id="add-team-member-form-cancel-button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              id="dialog-ok"
              disabled={!formikProps.isValid || !formikProps.dirty}
              type="submit"
              color="secondary"
              form="add-team-member-form"
              variant="contained"
            >
              Add
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default SingleUserAdd;
